/**
 * Profile Manager - Client-side anonymous profile management
 * Handles APID generation, localStorage sync, and backend API calls
 */

const APID_STORAGE_KEY = 'nysa-apid';
const PROFILE_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cryptographically secure random string
 */
const generateRandomString = (length = 8) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const randomValues = new Uint8Array(length);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(randomValues);
  } else {
    // Fallback for older browsers
    for (let i = 0; i < length; i++) {
      randomValues[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(randomValues)
    .map((byte) => chars[byte % chars.length])
    .join('');
};

/**
 * Generate new APID
 * Format: apid_[timestamp]_[random]
 */
export const generateApid = () => {
  const timestamp = Date.now();
  const random = generateRandomString(8);
  return `apid_${timestamp}_${random}`;
};

/**
 * Validate APID format
 */
export const isValidApid = (apid) => {
  const APID_REGEX = /^apid_[0-9]+_[a-z0-9]{8}$/;
  return typeof apid === 'string' && APID_REGEX.test(apid);
};

/**
 * Get or create APID from localStorage
 */
export const getOrCreateApid = () => {
  try {
    // Check if APID already exists
    const existingApid = localStorage.getItem(APID_STORAGE_KEY);

    if (existingApid && isValidApid(existingApid)) {
      console.log('[ProfileManager] Found existing APID:', existingApid);
      return existingApid;
    }

    // Generate new APID
    const newApid = generateApid();
    localStorage.setItem(APID_STORAGE_KEY, newApid);
    console.log('[ProfileManager] Generated new APID:', newApid);

    return newApid;
  } catch (error) {
    console.error('[ProfileManager] Error managing APID:', error);
    // Return temporary APID if localStorage fails
    return generateApid();
  }
};

/**
 * Get current APID without creating new one
 */
export const getCurrentApid = () => {
  try {
    const apid = localStorage.getItem(APID_STORAGE_KEY);
    return apid && isValidApid(apid) ? apid : null;
  } catch (error) {
    console.error('[ProfileManager] Error reading APID:', error);
    return null;
  }
};

/**
 * Delete APID from localStorage
 */
export const deleteApid = () => {
  try {
    localStorage.removeItem(APID_STORAGE_KEY);
    console.log('[ProfileManager] APID deleted');
    return true;
  } catch (error) {
    console.error('[ProfileManager] Error deleting APID:', error);
    return false;
  }
};

/**
 * Get user metadata for profile
 */
const getUserMetadata = () => {
  return {
    userAgent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language || 'en-US',
  };
};

/**
 * Initialize profile with backend
 */
export const initializeProfile = async (apiBaseUrl) => {
  const apid = getOrCreateApid();

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        apid,
        metadata: getUserMetadata(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Profile init failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[ProfileManager] Profile initialized:', data);

    return {
      apid: data.apid,
      isNewProfile: data.created,
      profile: data.profile,
    };
  } catch (error) {
    console.error('[ProfileManager] Failed to initialize profile:', error);
    // Return local-only profile
    return {
      apid,
      isNewProfile: true,
      profile: null,
      offline: true,
    };
  }
};

/**
 * Sync game state with backend
 */
export const syncGameState = async (apiBaseUrl, gameState) => {
  const apid = getCurrentApid();

  if (!apid) {
    console.warn('[ProfileManager] No APID found, skipping sync');
    return { success: false, reason: 'no_apid' };
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        apid,
        gameState,
      }),
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[ProfileManager] Game state synced:', data);

    return { success: true, syncedAt: data.syncedAt };
  } catch (error) {
    console.error('[ProfileManager] Failed to sync game state:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Record mission completion
 */
export const recordMissionCompletion = async (apiBaseUrl, missionData) => {
  const apid = getCurrentApid();

  if (!apid) {
    console.warn('[ProfileManager] No APID found, skipping mission record');
    return { success: false, reason: 'no_apid' };
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/mission-complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        apid,
        ...missionData,
      }),
    });

    if (!response.ok) {
      throw new Error(`Mission record failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('[ProfileManager] Mission recorded:', data);

    return { success: true, profile: data.profile };
  } catch (error) {
    console.error('[ProfileManager] Failed to record mission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get AI recommendation for this profile
 */
export const getAIRecommendation = async (apiBaseUrl) => {
  const apid = getCurrentApid();

  if (!apid) {
    return { useAI: false, reason: 'No profile', confidence: 0 };
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/ai-recommendation?apid=${apid}`);

    if (!response.ok) {
      throw new Error(`AI recommendation failed: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[ProfileManager] Failed to get AI recommendation:', error);
    return { useAI: false, reason: 'Error occurred', confidence: 0 };
  }
};

/**
 * Export all profile data (GDPR)
 */
export const exportProfileData = async (apiBaseUrl) => {
  const apid = getCurrentApid();

  if (!apid) {
    throw new Error('No profile found');
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/export?apid=${apid}`);

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[ProfileManager] Failed to export profile:', error);
    throw error;
  }
};

/**
 * Delete all profile data (GDPR)
 */
export const deleteProfileData = async (apiBaseUrl) => {
  const apid = getCurrentApid();

  if (!apid) {
    throw new Error('No profile found');
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/profile/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        apid,
        confirm: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`Deletion failed: ${response.status}`);
    }

    const data = await response.json();

    // Remove local APID
    deleteApid();

    return data;
  } catch (error) {
    console.error('[ProfileManager] Failed to delete profile:', error);
    throw error;
  }
};

/**
 * Setup automatic background sync
 */
export const setupAutoSync = (apiBaseUrl, getGameState) => {
  let syncInterval = null;

  const startSync = () => {
    if (syncInterval) {
      return; // Already running
    }

    syncInterval = setInterval(async () => {
      const gameState = getGameState();
      if (gameState) {
        await syncGameState(apiBaseUrl, gameState);
      }
    }, PROFILE_SYNC_INTERVAL);

    console.log('[ProfileManager] Auto-sync started');
  };

  const stopSync = () => {
    if (syncInterval) {
      clearInterval(syncInterval);
      syncInterval = null;
      console.log('[ProfileManager] Auto-sync stopped');
    }
  };

  // Start sync when page is visible
  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopSync();
    } else {
      startSync();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Initial start
  startSync();

  // Return cleanup function
  return () => {
    stopSync();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

/**
 * Download profile data as JSON file
 */
export const downloadProfileAsJson = async (apiBaseUrl) => {
  try {
    const data = await exportProfileData(apiBaseUrl);

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nysa-profile-${data.apid}-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('[ProfileManager] Failed to download profile:', error);
    throw error;
  }
};

const profileManager = {
  generateApid,
  isValidApid,
  getOrCreateApid,
  getCurrentApid,
  deleteApid,
  initializeProfile,
  syncGameState,
  recordMissionCompletion,
  getAIRecommendation,
  exportProfileData,
  deleteProfileData,
  setupAutoSync,
  downloadProfileAsJson,
};

export default profileManager;
